'use client'

import { Tabs as TabsPrimitive } from '@base-ui/react/tabs'

import type { VariantProps } from '@config/tailwind/cva'
import cn from '@config/tailwind/cn'
import { cva } from '@config/tailwind/cva'

function Tabs({ className, orientation = 'horizontal', ...props }: TabsPrimitive.Root.Props) {
  return (
    <TabsPrimitive.Root
      data-slot='tabs'
      data-orientation={orientation}
      className={cn('ui:group/tabs ui:flex ui:gap-2 ui:data-[orientation=horizontal]:flex-col', className)}
      {...props}
    />
  )
}

const tabsListVariants = cva(
  'ui:group/tabs-list ui:inline-flex ui:w-fit ui:items-center ui:justify-center ui:p-[3px] ui:group-data-[orientation=horizontal]/tabs:h-8 ui:group-data-[orientation=vertical]/tabs:h-fit ui:group-data-[orientation=vertical]/tabs:flex-col ui:data-[variant=line]:rounded-none',
  {
    variants: {
      variant: {
        default: 'ui:bg-blue-20 ui:rounded-full ui:text-blue-100',
        line: 'ui:gap-1 ui:bg-transparent ui:rounded-lg ui:text-neutral-black',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

function TabsList({
  className,
  variant = 'default',
  ...props
}: TabsPrimitive.List.Props & VariantProps<typeof tabsListVariants>) {
  return (
    <TabsPrimitive.List
      data-slot='tabs-list'
      data-variant={variant}
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    />
  )
}

function TabsTrigger({ className, ...props }: TabsPrimitive.Tab.Props) {
  return (
    <TabsPrimitive.Tab
      data-slot='tabs-trigger'
      className={cn(
        // Placeholder for css generation
        'ui:group-data-[variant=line]/tabs-list:text-grey-70 ui:group-data-[variant=line]/tabs-list:data-active:text-navy-50 ui:group-data-[variant=line]/tabs-list:hover:text-grey-60',
        "ui:relative ui:inline-flex ui:h-[calc(100%-1px)] ui:flex-1 ui:items-center ui:justify-center ui:gap-1.5 ui:border ui:border-transparent ui:px-1.5 ui:py-0.5 ui:text-sm ui:font-medium ui:whitespace-nowrap ui:transition-all ui:group-data-[orientation=vertical]/tabs:w-full ui:group-data-[orientation=vertical]/tabs:justify-start ui:focus-visible:ring-[3px] ui:focus-visible:outline-1 ui:disabled:pointer-events-none ui:disabled:opacity-50 ui:aria-disabled:pointer-events-none ui:aria-disabled:opacity-50 ui:group-data-[variant=default]/tabs-list:data-active:shadow-sm ui:group-data-[variant=line]/tabs-list:data-active:shadow-none ui:[&_svg]:pointer-events-none ui:[&_svg]:shrink-0 ui:[&_svg:not([class*='size-'])]:size-4",
        'ui:after:absolute ui:after:opacity-0 ui:after:transition-opacity ui:group-data-[orientation=horizontal]/tabs:after:inset-x-0 ui:group-data-[orientation=horizontal]/tabs:after:bottom-[-5px] ui:group-data-[orientation=horizontal]/tabs:after:h-0.5 ui:group-data-[orientation=vertical]/tabs:after:inset-y-0 ui:group-data-[orientation=vertical]/tabs:after:-right-1 ui:group-data-[orientation=vertical]/tabs:after:w-0.5 ui:group-data-[variant=line]/tabs-list:after:opacity-100',
        'ui:group-data-[variant=line]/tabs-list:bg-transparent ui:group-data-[variant=line]/tabs-list:data-active:bg-transparent',
        'ui:data-active:bg-neutral-white ui:data-active:text-navy-70',
        'ui:focus-visible:border-blue-100 ui:focus-visible:ring-blue-50 ui:focus-visible:outline-blue-100',
        'ui:data-active:after:bg-blue-100 ui:after:bg-navy-20',
        'ui:group-data-[variant=line]/tabs-list:text-neutral-black/60 ui:group-data-[variant=line]/tabs-list:hover:text-neutral-black',
        'ui:group-data-[variant=default]/tabs-list:text-grey-80',
        'ui:group-data-[variant=default]/tabs-list:text-blue-100/60 ui:group-data-[variant=default]/tabs-list:hover:text-blue-100 ui:group-data-[variant=default]/tabs-list:data-active:text-navy-70 ui:group-data-[variant=line]/tabs-list:hover:text-neutral-black ui:group-data-[variant=line]/tabs-list:data-active:text-neutral-black',
        'ui:group-data-[variant=default]/tabs-list:rounded-xl', // default variant rounded-full
        'ui:group-data-[variant=default]/tabs-list:rounded-full ui:group-data-[variant=line]/tabs-list:rounded-md',
        className,
      )}
      {...props}
    />
  )
}

function TabsContent({ className, ...props }: TabsPrimitive.Panel.Props) {
  return (
    <TabsPrimitive.Panel
      data-slot='tabs-content'
      className={cn('ui:flex-1 ui:text-sm ui:outline-none', className)}
      {...props}
    />
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent, tabsListVariants }
